import { createMemo, createSignal, For, untrack } from "solid-js";
import { Floating } from "~/common/floating";
import { Input } from "~/common/input";
import clsx from "~/util/html/style";

// A time of day as HH:MM (24-hour), the same value a native time input carries, picked from a list
// of times or typed ("9", "930", "9:30p", "21:30"). Replaces the browser's own time picker, which
// looks different in every browser and needs three separate segments typed.

const MINUTES_PER_DAY = 24 * 60;

const toValue = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

const toMinutes = (value: string) => {
  const [hours, minutes] = value.split(":").map(Number);
  return hours! * 60 + minutes!;
};

/** "13:05" as "1:05 PM". */
export function timeLabel(value: string): string {
  if (!/^\d{2}:\d{2}$/.test(value)) return "";
  const total = toMinutes(value);
  const hours = Math.floor(total / 60);
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(total % 60).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;
}

/**
 * The times typed text could mean, most likely first. An hour written with a leading zero or past 12
 * is a 24-hour time. Otherwise, with no AM or PM, it reads as working hours: 7 to 11 is the morning,
 * 12 to 6 the afternoon, so "5" offers 5:00 PM before 5:00 AM.
 */
export function parseTimeQuery(query: string): string[] {
  const match = /^(\d{1,2}):?(\d{2})?\s*(a|am|p|pm)?$/.exec(query.trim().toLowerCase());
  if (!match) return [];
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  const meridiem = match[3]?.[0];
  if (minutes > 59) return [];

  if (meridiem) {
    if (hours < 1 || hours > 12) return [];
    const base = hours % 12;
    return [toValue((meridiem === "p" ? base + 12 : base) * 60 + minutes)];
  }
  if (hours > 23) return [];
  // "06:00", "0930" and "13:15" are already 24-hour times.
  if (match[1]!.startsWith("0") || hours > 12) return [toValue(hours * 60 + minutes)];

  const morning = toValue((hours % 12) * 60 + minutes);
  const afternoon = toValue(((hours % 12) + 12) * 60 + minutes);
  return hours >= 7 && hours <= 11 ? [morning, afternoon] : [afternoon, morning];
}

export function TimeInput(props: {
  id?: string;
  /** HH:MM, or "" for no time. */
  value: string;
  onChange: (value: string) => void;
  /** Minutes between the listed times; any minute can still be typed. */
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  width?: "full" | "unset" | "fit";
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const [open, setOpen] = createSignal(false);
  // What the manager is typing, until it is picked or committed on blur; null shows the value.
  const [draft, setDraft] = createSignal<string | null>(null);
  const [highlighted, setHighlighted] = createSignal(0);
  // Set once the arrow keys move the highlight: only then, or after typing, does Enter pick it.
  const [navigated, setNavigated] = createSignal(false);
  const [listWidth, setListWidth] = createSignal(0);
  let containerRef!: HTMLDivElement;
  let inputRef!: HTMLInputElement;
  let listRef: HTMLDivElement | undefined;

  const slots = createMemo(() => {
    const step = props.step ?? 15;
    return Array.from({ length: MINUTES_PER_DAY / step }, (_, index) => toValue(index * step));
  });

  // While typing: what the text could mean, then the listed times that read the same way.
  const options = createMemo(() => {
    const query = draft();
    if (query === null || query.trim() === "") return slots();
    const exact = parseTimeQuery(query);
    const needle = query.replace(/\s/g, "").toLowerCase();
    const similar = slots().filter(
      (slot) =>
        !exact.includes(slot) &&
        timeLabel(slot).replace(/\s/g, "").toLowerCase().startsWith(needle),
    );
    return [...exact, ...similar];
  });

  const scrollToHighlighted = () =>
    queueMicrotask(() => {
      const index = untrack(highlighted);
      listRef?.children[index]?.scrollIntoView({ block: "nearest" });
    });

  // Opening lands on the value, or the first listed time after it, so the list starts where the
  // manager is rather than at midnight.
  const openList = () => {
    const value = untrack(() => props.value) || "08:00";
    const index = slots().findIndex((slot) => slot >= value);
    setHighlighted(index === -1 ? 0 : index);
    setNavigated(false);
    setListWidth(containerRef.offsetWidth);
    setOpen(true);
    scrollToHighlighted();
  };

  const close = () => {
    setOpen(false);
    setDraft(null);
    setNavigated(false);
  };

  const pick = (value: string) => {
    if (value !== props.value) props.onChange(value);
    close();
  };

  // Leaving the field keeps a typed time (its likeliest reading), clears an emptied one, and
  // forgets text that isn't a time.
  const commit = () => {
    const query = draft();
    if (query === null) return close();
    if (query.trim() === "") return pick("");
    const [likeliest] = parseTimeQuery(query);
    if (likeliest) return pick(likeliest);
    close();
  };

  const move = (by: number) => {
    const count = options().length;
    if (count === 0) return;
    if (!open()) openList();
    setHighlighted((index) => (index + by + count) % count);
    setNavigated(true);
    scrollToHighlighted();
  };

  return (
    <div ref={containerRef} class={clsx(props.width === "full" && "w-full")}>
      <Input
        ref={(element) => (inputRef = element)}
        id={props.id}
        type="text"
        inputmode="text"
        autocomplete="off"
        role="combobox"
        aria-expanded={open()}
        aria-autocomplete="list"
        aria-describedby={props["aria-describedby"]}
        aria-invalid={props["aria-invalid"]}
        beforeIcon="clock"
        placeholder={props.placeholder ?? "Pick a time"}
        disabled={props.disabled}
        width={props.width ?? "full"}
        value={draft() ?? timeLabel(props.value)}
        onFocus={() => {
          openList();
          setTimeout(() => inputRef.select(), 0);
        }}
        onClick={() => {
          if (!open()) openList();
        }}
        onInput={(event) => {
          setDraft(event.currentTarget.value);
          setHighlighted(0);
          setOpen(true);
        }}
        // Commit at once, so a Save clicked straight after typing sends the typed time. A press on the
        // list never blurs the field (its mousedown is prevented), so a pick still lands.
        onBlur={() => commit()}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            move(1);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            move(-1);
          } else if (event.key === "Enter") {
            event.preventDefault();
            // Enter picks the highlighted time only if the manager typed or moved to it: on a field
            // they just opened it keeps the value, and an emptied field clears.
            const query = draft();
            const typed = query !== null && query.trim() !== "";
            const option = open() && (typed || navigated()) ? options()[highlighted()] : undefined;
            if (option) pick(option);
            else commit();
          } else if (event.key === "Escape" && open()) {
            // Only this list closes; a popover around the field stays open.
            event.preventDefault();
            event.stopPropagation();
            close();
          }
        }}
      />
      <Floating
        reference={() => containerRef}
        open={() => open() && options().length > 0}
        placement="bottom-start"
      >
        <div
          ref={(element) => (listRef = element)}
          role="listbox"
          onMouseDown={(event) => event.preventDefault()}
          class="max-h-64 overflow-auto overscroll-none rounded-xl border border-brand-divider bg-white text-sm shadow-lg"
          style={{ "min-width": `${listWidth()}px` }}
        >
          <For each={options()}>
            {(option, index) => (
              <button
                type="button"
                role="option"
                aria-selected={option === props.value}
                tabIndex={-1}
                class={clsx(
                  "flex w-full cursor-pointer px-3 py-2 text-left tabular-nums transition-colors",
                  index() === highlighted() ? "bg-brand-light" : "hover:bg-brand-hover",
                  option === props.value && "font-medium",
                )}
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(option);
                }}
                onMouseEnter={() => setHighlighted(index())}
              >
                {timeLabel(option)}
              </button>
            )}
          </For>
        </div>
      </Floating>
    </div>
  );
}
